import { env, loadRuntimeConfig } from '@/shared/config';

import { VIEW } from './events';
import type { AppEvent } from './events';

/**
 * Аналитика мини-аппа — Яндекс Метрика (решение владельца 02.09.2026).
 *
 * **Почему Метрика.** Данные игроков остаются в России — исходная причина
 * выбирать self-hosted PostHog отпадает вместе с самим инстансом, который
 * пришлось бы поднимать и обслуживать. Обязательная часть ТЗ — активации,
 * DAU/WAU/MAU, ретеншен D1/D7/D30 — считается в Метрике штатными отчётами,
 * без единой строки кода с нашей стороны.
 *
 * **Два способа отправки, и это не дублирование.**
 *
 * `hit` — просмотр страницы. Метрика считает их НЕУНИКАЛЬНО, а в ТЗ
 * «количество неуникальных заходов» стоит у пяти экранов, у выдач экрана
 * подписки, у открытий Дрибла и у кликов на результаты. Всё это уходит
 * просмотрами, и разрезы читаются в отчёте «Популярное» без настройки.
 *
 * `reachGoal` — цель. Метрика считает цели по ВИЗИТАМ, то есть уникально
 * за визит. Ровно это просит ТЗ у кликов «Подпишись на канал».
 *
 * Перепутать местами — молча получить не тот показатель: цель на выдаче
 * экрана посчитала бы визиты вместо выдач и занизила бы число.
 *
 * **Без счётчика не делает ничего.** Ключа нет — работает всё, кроме
 * аналитики. Это держит e2e и демо-режим вне сети без единого условия
 * в вызывающем коде.
 *
 * Что сюда НЕ уходит: телефон, имя, `telegram_id`. Игрок в отчётах —
 * внутренний `user.id`, и ничего больше.
 *
 * Серверная половина ТЗ (Очки, Фанкоины, Фрибеты, Прогнозы, Ачивки,
 * Карьера, учтённая активность) в Метрику не идёт вовсе: она считается
 * по лентам начислений в дашборде админки — `docs/analytics.md`.
 */
type Params = Record<string, string | number | boolean | null>;

interface MetrikaQueue {
  (...args: unknown[]): void;
  a?: unknown[][];
  l?: number;
}

declare global {
  interface Window {
    ym?: MetrikaQueue;
  }
}

/**
 * Адрес тега с номером счётчика — форма, которую Метрика выдаёт сейчас.
 * Номер в адресе позволяет ей отдать сборку под конкретный счётчик,
 * а нам — узнать уже вставленный тег и не вставить второй.
 */
const tagUrl = (id: number): string =>
  `https://mc.yandex.ru/metrika/tag.js?id=${String(id)}`;

let counter: number | null = null;

/**
 * Очередь до загрузки скрипта — часть официального сниппета Метрики.
 *
 * Вызовы, случившиеся раньше загрузки `tag.js`, копятся в `ym.a`
 * и отправляются, когда он приедет. Без неё первый экран терялся бы
 * всегда: он открывается быстрее, чем грузится сторонний скрипт.
 */
function ensureQueue(): MetrikaQueue {
  if (!window.ym) {
    const queue: MetrikaQueue = (...args: unknown[]) => {
      (queue.a ??= []).push(args);
    };
    queue.l = Date.now();
    window.ym = queue;
  }
  return window.ym;
}

export async function initAnalytics(): Promise<void> {
  if (counter !== null) return;

  // Сборочная переменная имеет приоритет: ею пользуются локальная
  // разработка и e2e, где никакого `/config.json` нет. На сервере она
  // пуста, и номер приходит из рантайм-конфига — так один образ
  // работает на любой площадке
  const runtime = await loadRuntimeConfig();
  const id = env.metrikaCounterId ?? runtime.metrikaCounterId;
  if (id === null) return;

  const ym = ensureQueue();
  ym(id, 'init', {
    // Первый просмотр шлём сами: у SPA автоматический срабатывает один
    // раз за загрузку и переходы между вкладками не видит
    defer: true,
    // Клики по внешним ссылкам — уход в канал и к букмекеру
    trackLinks: true,
    // Вебвизор — запись экрана игрока. В мини-аппе с персональными
    // данными это наблюдение, а заказчик просил метрики
    webvisor: false,
    // Карта кликов собирает координаты каждого касания. Та же причина
    clickmap: false,
    accurateTrackBounce: true,
  });

  // Тег мог вставить кто-то до нас — например, при горячей перезагрузке
  // в разработке. Второй экземпляр Метрики удваивает просмотры молча
  const src = tagUrl(id);
  const already = Array.from(document.scripts).some((s) => s.src === src);
  if (!already) {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    document.head.appendChild(script);
  }

  counter = id;

  // Запуск отмечаем СРАЗУ, до авторизации.
  //
  // Иначе визит не регистрируется вовсе, пока игрок не вошёл: `defer`
  // отменяет автоматический первый просмотр, а экраны шлёт трекер уже
  // из-под входа. Сломается авторизация — Метрика покажет ноль визитов,
  // и «никто не пришёл» станет неотличимо от «никто не смог войти».
  // Разница между `/e/launch` и `/home` и есть эта воронка.
  trackView(VIEW.launch);
}

/**
 * Игрок опознан. Идентификатор — внутренний `user.id`, не `telegram_id`.
 *
 * Вызовов ДВА, и они делают разное.
 *
 * `setUserID` только СВЯЗЫВАЕТ наш идентификатор с ClientID Метрики —
 * склеивает устройства одного игрока. Самого значения в отчётах после
 * него не видно, и это неочевидно: кажется, что раз идентификатор
 * передан, он где-то отображается.
 *
 * Чтобы значение попало в отчёт «Параметры посетителей» и его можно было
 * сверить с нашей базой, тот же id уходит ещё и параметром посетителя
 * под зарезервированным ключом `UserID`. Без этого сшивка Метрики с базой,
 * ради которой всё и делается, остаётся невозможной.
 *
 * Оба вызова идут при КАЖДОМ входе, а не один раз при регистрации:
 * Метрика связывает идентификаторы только для тех визитов, в которых
 * методы действительно вызывались.
 */
export function identifyPlayer(userId: string, properties: Params): void {
  if (counter === null || !window.ym) return;
  window.ym(counter, 'setUserID', userId);
  window.ym(counter, 'userParams', { ...properties, UserID: userId });
}

/**
 * Просмотр «страницы» — считается неуникально.
 *
 * Путь синтетический там, где экрана в роутере нет: модальное окно
 * подписки и открытие Дрибла страницами не являются, но в отчёте
 * «Популярное» им нужна своя строка. Префикс отделяет их от настоящих
 * маршрутов, чтобы «заходы в Главную» не смешались с показами окна.
 */
export function trackView(path: string): void {
  if (counter === null || !window.ym) return;
  window.ym(counter, 'hit', path);
}

/** Цель — считается по визитам, то есть уникально за визит. */
export function track(event: AppEvent, params?: Params): void {
  if (counter === null || !window.ym) return;
  window.ym(counter, 'reachGoal', event, params);
}
